import { useQuery } from "@apollo/client";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import SearchBar from "components/base/SearchBar";
import Spinner from "components/base/Spinner";
import { Alert } from "components/base/Toast";
import PageV2 from "components/shared/PageV2";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FETCH_E_FILING_COLLECTION } from "./graphql";

function parseCollection(value) {
  if (!value) return {};
  if (typeof value === "object") return value;
  if (typeof value !== "string") return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

const CPSCProductsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams({ q: "" });
  const [refreshing, setRefreshing] = useState(false);
  const q = searchParams.get("q") || "";
  const normalizedQ = q.trim().toLowerCase();

  const { loading, error, data, refetch } = useQuery(
    FETCH_E_FILING_COLLECTION,
    {
      variables: { refresh: false },
      fetchPolicy: "network-only",
    },
  );

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  const tokenExpiration = JSON.parse(data.eFilingGetTokenExpiration);
  const tokenExpiresAt = tokenExpiration?.tokenExpiration
    ? new Date(tokenExpiration.tokenExpiration).toLocaleString()
    : "-";
  const collection = parseCollection(data?.eFilingExportCollection);
  const lastSuccessfulReadAt = collection?.lastSuccessfulReadAt
    ? new Date(collection.lastSuccessfulReadAt).toLocaleString()
    : null;
  const products = Array.isArray(collection?.productList)
    ? collection.productList
    : [];

  const filteredProducts = products.filter((item) => {
    if (!normalizedQ) return true;
    const primaryProductId = String(item?.coreProduct?.primaryProductId || "")
      .trim()
      .toLowerCase();
    const name = String(item?.coreProduct?.name || "")
      .trim()
      .toLowerCase();
    return primaryProductId.includes(normalizedQ) || name.includes(normalizedQ);
  });

  const refreshHandler = async () => {
    setRefreshing(true);
    try {
      await refetch({ refresh: true });
      Alert("success", "CPSC products refreshed successfully.");
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <PageV2
      backTo="./.."
      title="CPSC Products"
      className="h-screen"
      toolbar={
        <div className="flex items-center space-x-6">
          {lastSuccessfulReadAt && (
            <div className="text-gray-500 dark:text-gray-400">
              Last read at: {lastSuccessfulReadAt}
            </div>
          )}
          <Button onClick={refreshHandler} loading={refreshing}>
            Refresh
          </Button>
        </div>
      }
    >
      <div className="h-full flex flex-col">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p>
              This page shows the list of products that are registered with the
              U.S. Consumer Product Safety Commission (CPSC). Certificates are
              managed by Stella in the{" "}
              <a
                href="https://efiling.saferproducts.gov/efiling/"
                target="_blank"
                rel="noopener noreferrer"
              >
                CPSC EFiling System
              </a>
              .
            </p>
            <p>
              To have better performance, WIS caches the data from the CPSC
              EFiling System for 3 hours. If you need the latest data
              immediately after you update the certificates in the CPSC EFiling
              System, please click the "<b>Refresh</b>" button to fetch the
              latest data from the CPSC EFiling System.
            </p>
            <p>
              If you need to bulk import products into the CPSC EFiling System,
              you can download this{" "}
              <a
                href="https://cdn.waboba.com/public/cpsc_products_import_template.csv"
                target="_blank"
                rel="noopener noreferrer"
              >
                CPSC product import template
              </a>
              .
            </p>
          </div>
        </div>

        <div className="mt-4">
          <SearchBar
            initialQuery={q}
            placeholder="Search by product SKU or name"
            onChange={(nextQ) => setSearchParams({ q: nextQ })}
          />
        </div>

        <div className="card p-0 flex-1 overflow-auto flex flex-col mt-6">
          <div className="flex-1 overflow-auto relative">
            {filteredProducts.length === 0 ? (
              <div className="p-8 text-sm text-gray-500">
                No CPSC products found.
              </div>
            ) : (
              <div className="whitespace-nowrap flex flex-col h-full">
                <table className="w-full">
                  <thead>
                    <tr className="sticky top-0 backdrop-blur-xl dark:bg-opacity-90 z-10 bg-gray-50 dark:bg-gray-800">
                      <th className="px-4 py-3 text-left">
                        CPSC Products ({filteredProducts.length})
                      </th>
                      <th className="px-3 py-3 text-left">Version</th>
                      <th className="px-3 py-3 text-left">Certificate Type</th>
                      <th className="px-3 py-3 text-left">Manufacturer</th>
                      <th className="px-3 py-3 text-left">Last Test Date</th>
                      <th className="px-3 py-3 text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProducts.map((item, index) => {
                      const coreProduct = item?.coreProduct || {};
                      const productMetadata = item?.productMetadata || {};
                      const displayVersion =
                        coreProduct?.versionId ||
                        productMetadata?.completedVersions?.join(", ") ||
                        "-";
                      return (
                        <tr
                          key={`${coreProduct?.primaryProductId || "product"}-${index}`}
                          className="border-y border-gray-100 dark:border-gray-700"
                        >
                          <td className="px-4 py-2">
                            <div>
                              <div className="font-semibold">
                                {coreProduct?.primaryProductId || "-"}
                              </div>
                              <div className="opacity-70">
                                {coreProduct?.name || "-"}
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-2">{displayVersion}</td>
                          <td className="px-3 py-2">
                            {coreProduct?.certificateType || "-"}
                          </td>
                          <td className="px-3 py-2">
                            {coreProduct?.manufacturer?.name || "-"}
                          </td>
                          <td className="px-3 py-2">
                            {coreProduct?.lastTestDate || "-"}
                          </td>
                          <td className="px-3 py-2">
                            {productMetadata?.status || "-"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <div className="mt-2 opacity-70 italic text-right">
          API token expires at: {tokenExpiresAt}.
        </div>
      </div>
    </PageV2>
  );
};

export default CPSCProductsPage;
